import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Maximize2 } from "lucide-react";
import Header from "@/components/apex/Header";
import Footer from "@/components/apex/Footer";
import MediaViewer from "@/components/apex/MediaViewer";
import DesignThumb from "@/components/apex/DesignThumb";
import { useLang } from "@/i18n/LanguageContext";
import { gallery, galleryCategories, visualizerCategories, waLink } from "@/content/site";
import { conceptDesigns } from "@/content/designs";
import { Label, WhatsAppIcon, Arrow } from "@/components/apex/primitives";

/* صفحة التصاميم الكاملة: أعمال APEX الحقيقية + مكتبة التصاميم المولَّدة */
export default function Designs() {
  const { t, isRtl } = useLang();
  const [cat, setCat] = useState("all");
  const [view, setView] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const all = useMemo(() => [...gallery, ...conceptDesigns], []);
  const cats = useMemo(() => {
    const seen = new Set();
    return [galleryCategories[0], ...galleryCategories.slice(1), ...visualizerCategories].filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, []);
  const items = cat === "all" ? all : all.filter((g) => g.category === cat);

  const askMsg = (item) =>
    isRtl
      ? `مرحبًا APEX، أعجبني تصميم «${item.title_ar}» وأرغب بمعرفة الخيارات والسعر المناسب لمدخلي.`
      : `Hello APEX, I liked the "${item.title_en}" design and would like to know the options and a suitable price for my entrance.`;

  return (
    <>
      <Header />
      <main data-testid="designs-page" className="bg-[var(--cream)] pt-28 sm:pt-32">
        <section className="mx-auto max-w-[1400px] px-5 pb-20 sm:px-8">
          <Link to="/" data-testid="designs-back" className="ap-link text-[14px]">
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />
            {t.nav.home}
          </Link>
          <div className="mt-6 max-w-2xl">
            <Label>{t.gallery.label}</Label>
            <h1 className="ap-display mt-5 text-[2.3rem] text-[var(--text)] sm:text-[3rem] lg:text-[3.6rem]">{t.gallery.title}</h1>
            <p className="mt-4 text-[15px] leading-[1.9] text-[var(--muted)] sm:text-base">{t.gallery.body}</p>
          </div>

          <div role="group" aria-label={t.gallery.filterAria} className="mt-8 flex flex-wrap gap-2.5">
            {cats.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                aria-pressed={cat === c.id}
                data-testid={`designs-filter-${c.id}`}
                className={`ap-filter ${cat === c.id ? "ap-filter-on" : ""}`}
              >
                {isRtl ? c.label_ar : c.label_en}
              </button>
            ))}
            <span className="ms-auto self-center text-[13px] text-[var(--muted)]" data-testid="designs-count">
              {items.length}
            </span>
          </div>

          <motion.div layout className="mt-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            <AnimatePresence mode="popLayout">
              {items.map((item, i) => (
                <motion.article
                  layout
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, delay: Math.min(i, 12) * 0.03, ease: [0.16, 1, 0.3, 1] }}
                  className="ap-card ap-card-hover group flex flex-col p-2.5"
                  data-testid={`designs-card-${item.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setView(item)}
                    aria-label={`${t.gallery.view}: ${isRtl ? item.title_ar : item.title_en}`}
                    className="ap-img relative block aspect-[3/4] w-full"
                  >
                    <DesignThumb item={item} alt={isRtl ? item.alt_ar : item.alt_en} />
                    <span className="absolute start-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[10.5px] font-semibold text-[var(--text)] shadow">
                      {item.concept ? t.gallery.conceptTag : t.materials.realTag}
                    </span>
                    <span className="absolute end-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-[var(--charcoal)] opacity-0 shadow transition-opacity duration-500 group-hover:opacity-100">
                      <Maximize2 className="h-4 w-4" />
                    </span>
                  </button>
                  <div className="flex flex-1 flex-col px-2 pb-2 pt-4">
                    <h2 className="ap-display text-[1.05rem] text-[var(--text)] sm:text-[1.15rem]">
                      {isRtl ? item.title_ar : item.title_en}
                    </h2>
                    <p className="mt-1.5 line-clamp-2 flex-1 text-[12.5px] leading-[1.7] text-[var(--muted)]">
                      {isRtl ? item.finish_ar : item.finish_en}
                    </p>
                    <a
                      href={waLink(askMsg(item))}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`designs-ask-${item.id}`}
                      className="ap-link mt-3 text-[13px]"
                    >
                      <WhatsAppIcon className="h-4 w-4 text-[var(--wa)]" />
                      {t.gallery.ask}
                      <Arrow className="!h-3.5 !w-3.5" />
                    </a>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        </section>
      </main>
      <Footer />
      <AnimatePresence>{view && <MediaViewer item={view} onClose={() => setView(null)} />}</AnimatePresence>
    </>
  );
}
